import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import {
    Select, Row, Col, Spin, Space, Empty, Table, Tag, Progress, Tooltip, Button, Pagination,
} from "antd";
import {
    WarningOutlined, SearchOutlined, CalendarOutlined, LeftOutlined, RightOutlined,
    BankOutlined, ApartmentOutlined,
} from "@ant-design/icons";
import { Input } from "antd";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;
const { Search } = Input;


export default function LeaveUtilization() {
    const { authData, backUrl } = useContext(AuthContext);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [organisations, setOrganisations] = useState([]);
    const [organisationId, setOrganisationId] = useState("ALL");
    const [departments, setDepartments] = useState([]);
    const [departmentId, setDepartmentId] = useState("ALL");
    const [searchTerm, setSearchTerm] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 6;

    const tableWrapperRef = useRef(null);
    const greyTrackRef = useRef(null);
    const isSyncingRef = useRef(false);

    const scrollTable = (direction) => {
        if (tableWrapperRef.current) {
            const el = tableWrapperRef.current.querySelector(".ant-table-content") ||
                       tableWrapperRef.current.querySelector(".ant-table-body") ||
                       tableWrapperRef.current;
            if (el) {
                el.scrollBy({ left: direction === "left" ? -240 : 240, behavior: "smooth" });
            }
        }
    };

    const handleTableScroll = (e) => {
        if (isSyncingRef.current) return;
        isSyncingRef.current = true;
        const tableEl = e.target;
        if (greyTrackRef.current && tableEl) {
            const maxTableScroll = tableEl.scrollWidth - tableEl.clientWidth;
            if (maxTableScroll > 0) {
                const ratio = tableEl.scrollLeft / maxTableScroll;
                const maxTrackScroll = greyTrackRef.current.scrollWidth - greyTrackRef.current.clientWidth;
                greyTrackRef.current.scrollLeft = ratio * maxTrackScroll;
            }
        }
        setTimeout(() => { isSyncingRef.current = false; }, 30);
    };

    const handleTrackScroll = (e) => {
        if (isSyncingRef.current) return;
        isSyncingRef.current = true;
        const trackEl = e.target;
        if (tableWrapperRef.current && trackEl) {
            const tableEl = tableWrapperRef.current.querySelector(".ant-table-content") ||
                            tableWrapperRef.current.querySelector(".ant-table-body");
            if (tableEl) {
                const maxTrackScroll = trackEl.scrollWidth - trackEl.clientWidth;
                if (maxTrackScroll > 0) {
                    const ratio = trackEl.scrollLeft / maxTrackScroll;
                    const maxTableScroll = tableEl.scrollWidth - tableEl.clientWidth;
                    tableEl.scrollLeft = ratio * maxTableScroll;
                }
            }
        }
        setTimeout(() => { isSyncingRef.current = false; }, 30);
    };

    useEffect(() => {
        if (!loading && tableWrapperRef.current) {
            const el = tableWrapperRef.current.querySelector(".ant-table-content") ||
                       tableWrapperRef.current.querySelector(".ant-table-body");
            if (el) {
                el.addEventListener("scroll", handleTableScroll);
                return () => el.removeEventListener("scroll", handleTableScroll);
            }
        }
    }, [loading, data, currentPage]);


    useEffect(() => {
        axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setOrganisations(res.data.results || res.data || []))
          .catch(() => {});
    }, [backUrl, authData?.access]);

    useEffect(() => {
        const orgParam = organisationId !== "ALL" ? `?organisation=${organisationId}` : "";
        axios.get(`${backUrl}/api/emp/departments/${orgParam}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setDepartments(res.data.results || res.data || []))
          .catch(() => setDepartments([]));
        setDepartmentId("ALL");
    }, [organisationId, backUrl, authData?.access]);

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (organisationId !== "ALL") params.organisation_id = organisationId;
        if (departmentId !== "ALL") params.department_id = departmentId;

        axios.get(`${backUrl}/api/reports/leave-utilization/`, {
            params,
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setData(Array.isArray(res.data) ? res.data : []))
          .catch(() => setData([]))
          .finally(() => setLoading(false));
    }, [organisationId, departmentId, backUrl, authData?.access]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, organisationId, departmentId]);

    const filteredData = data.filter(emp =>
        (emp.employee_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.employee_code || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.department || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    const pageData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    /* Summary counts — count low balance only if employee has a positive entitlement */
    const lowClBal   = data.filter(e => (e.cl_total > 0) && (e.cl_balance ?? 0) <= 2).length;
    const lowSlBal   = data.filter(e => (e.sl_total > 0) && (e.sl_balance ?? 0) <= 2).length;
    const rawTotal   = data.reduce((s, e) => s + Number(e.total_used ?? ((e.cl_used || 0) + (e.sl_used || 0))), 0);
    const totalUsed  = Math.round(rawTotal * 10) / 10;

    /* Helper: balance color */
    const balColor = (bal) => {
        if (bal === undefined || bal === null) return "#64748b";
        if (bal <= 1) return "#dc2626";
        if (bal <= 2) return "#f59e0b";
        return "#10b981";
    };

    const columns = [
        {
            title: "Code",
            dataIndex: "employee_code",
            width: 85,
            render: v => (
                <Tag style={{ fontWeight: 700, borderRadius: 6, margin: 0, fontSize: 11 }}>{v || "-"}</Tag>
            ),
        },
        {
            title: "Employee",
            dataIndex: "employee_name",
            width: 170,
            ellipsis: true,
            render: (v, r) => (
                <div>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 13 }}>{v}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.department || "-"}</div>
                </div>
            ),
        },
        {
            title: "CL Used / Total",
            key: "cl",
            width: 145,
            render: (_, r) => {
                const used  = r.cl_used  ?? 0;
                const total = r.cl_total ?? 12;
                const pct   = Math.min(Math.round((used / Math.max(total, 1)) * 100), 100);
                return (
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#334155" }}>
                                {used} / {total} days
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: pct >= 80 ? "#ef4444" : "#64748b" }}>
                                {pct}%
                            </span>
                        </div>
                        <Progress
                            percent={pct}
                            showInfo={false}
                            strokeColor={pct >= 80 ? "#ef4444" : pct >= 50 ? "#f59e0b" : "#10b981"}
                            trailColor="#e2e8f0"
                            size={[undefined, 6]}
                            style={{ margin: 0 }}
                        />
                    </div>
                );
            },
        },
        {
            title: "SL Used / Total",
            key: "sl",
            width: 145,
            render: (_, r) => {
                const used  = r.sl_used  ?? 0;
                const total = r.sl_total ?? 12;
                const pct   = Math.min(Math.round((used / Math.max(total, 1)) * 100), 100);
                return (
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#334155" }}>
                                {used} / {total} days
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: pct >= 80 ? "#ef4444" : "#64748b" }}>
                                {pct}%
                            </span>
                        </div>
                        <Progress
                            percent={pct}
                            showInfo={false}
                            strokeColor={pct >= 80 ? "#ef4444" : pct >= 50 ? "#f59e0b" : "#3b82f6"}
                            trailColor="#e2e8f0"
                            size={[undefined, 6]}
                            style={{ margin: 0 }}
                        />
                    </div>
                );
            },
        },
        {
            title: "CL Balance",
            dataIndex: "cl_balance",
            width: 95,
            align: "center",
            render: v => {
                const color = balColor(v);
                return (
                    <Tooltip title={v <= 2 ? "Low CL balance — employee may exhaust leave soon" : undefined}>
                        <Tag style={{
                            fontWeight: 800, borderRadius: 8, margin: 0,
                            color, background: `${color}18`, border: `1px solid ${color}40`,
                            fontSize: 12,
                        }}>
                            {v ?? 0}
                            {v <= 2 && v !== undefined && (
                                <WarningOutlined style={{ marginLeft: 4, fontSize: 10 }} />
                            )}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: "SL Balance",
            dataIndex: "sl_balance",
            width: 95,
            align: "center",
            render: v => {
                const color = balColor(v);
                return (
                    <Tooltip title={v <= 2 ? "Low SL balance — employee may exhaust leave soon" : undefined}>
                        <Tag style={{
                            fontWeight: 800, borderRadius: 8, margin: 0,
                            color, background: `${color}18`, border: `1px solid ${color}40`,
                            fontSize: 12,
                        }}>
                            {v ?? 0}
                            {v <= 2 && v !== undefined && (
                                <WarningOutlined style={{ marginLeft: 4, fontSize: 10 }} />
                            )}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: "Total Used",
            key: "total_used",
            width: 105,
            align: "center",
            render: (_, r) => {
                const total = r.total_used ?? ((r.cl_used || 0) + (r.sl_used || 0));
                return (
                    <Tag style={{
                        fontWeight: 800, borderRadius: 8, margin: 0,
                        background: "#e0f2fe", color: "#0284c7", border: "1px solid #7dd3fc",
                    }}>
                        {total} days
                    </Tag>
                );
            },
        },
    ];

    return (
        <div style={{
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            borderRadius: 16, border: "1px solid #e2e8f0",
            boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
            overflow: "hidden", height: "100%",
        }}>
            <div style={{ height: 3.5, background: "linear-gradient(90deg, #0e497b, #3b82f6)" }} />
            <div style={{ padding: "16px 20px" }}>
                {/* Header */}
                <Row justify="space-between" align="middle" gutter={[10, 10]} style={{ marginBottom: 14 }}>
                    <Col>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <CalendarOutlined style={{ color: "#0e497b", fontSize: 18 }} />
                            <div>
                                <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>
                                    Leave Utilization Tracker
                                </div>
                                <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                                    CL &amp; SL balance, usage ratios and alerts
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col>
                        <Space size={6} wrap align="middle">
                            <Search
                                placeholder="Search Employee / Code..."
                                onSearch={setSearchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{ width: 170 }}
                                allowClear
                                size="middle"
                                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                            />
                            <Select
                                value={organisationId}
                                onChange={(v) => setOrganisationId(v || "ALL")}
                                showSearch
                                optionFilterProp="children"
                                allowClear
                                onClear={() => setOrganisationId("ALL")}
                                popupMatchSelectWidth={false}
                                placeholder="Filter Organisation"
                                style={{ width: 190 }}
                                size="middle"
                            >
                                <Option value="ALL">
                                    <Space><BankOutlined style={{ color: "#0e497b" }} /> <span>All Organisations ({organisations.length})</span></Space>
                                </Option>
                                {organisations.map(o => (
                                    <Option key={o.id} value={o.id}>
                                        <Space><BankOutlined style={{ color: "#0e497b" }} /> <span>{o.name}</span></Space>
                                    </Option>
                                ))}
                            </Select>

                            <Select
                                value={departmentId}
                                onChange={(val) => setDepartmentId(val || "ALL")}
                                showSearch
                                optionFilterProp="children"
                                allowClear
                                onClear={() => setDepartmentId("ALL")}
                                popupMatchSelectWidth={false}
                                placeholder="Filter Department"
                                style={{ width: 180 }}
                                size="middle"
                            >
                                <Option value="ALL">
                                    <Space><ApartmentOutlined style={{ color: "#2563eb" }} /> <span>All Departments ({departments.length})</span></Space>
                                </Option>
                                {departments.map(d => (
                                    <Option key={d.id} value={d.id}>
                                        <Space><ApartmentOutlined style={{ color: "#2563eb" }} /> <span>{d.name}</span></Space>
                                    </Option>
                                ))}
                            </Select>


                            {/* Navigation Scroll Buttons */}
                            <Space.Compact style={{ marginLeft: 4 }}>
                                <Tooltip title="Scroll Left">
                                    <Button
                                        icon={<LeftOutlined />}
                                        size="middle"
                                        onClick={() => scrollTable("left")}
                                        style={{ borderRadius: "8px 0 0 8px", background: "#f1f5f9", borderColor: "#cbd5e1" }}
                                    />
                                </Tooltip>
                                <Tooltip title="Scroll Right">
                                    <Button
                                        icon={<RightOutlined />}
                                        size="middle"
                                        onClick={() => scrollTable("right")}
                                        style={{ borderRadius: "0 8px 8px 0", background: "#f1f5f9", borderColor: "#cbd5e1" }}
                                    />
                                </Tooltip>
                            </Space.Compact>
                        </Space>
                    </Col>
                </Row>

                {/* Summary Strip */}
                {!loading && data.length > 0 && (
                    <Row gutter={[10, 8]} style={{ marginBottom: 14 }}>
                        {[
                            { label: "Total Employees", val: data.length, color: "#0e497b", bg: "#eff6ff", border: "#bfdbfe" },
                            { label: "Total Leave Used", val: `${totalUsed} days`, color: "#7c3aed", bg: "#faf5ff", border: "#ddd6fe" },
                            { label: "Low CL Balance", val: lowClBal, color: lowClBal > 0 ? "#f59e0b" : "#10b981", bg: lowClBal > 0 ? "#fffbeb" : "#f0fdf4", border: lowClBal > 0 ? "#fde68a" : "#bbf7d0" },
                            { label: "Low SL Balance", val: lowSlBal, color: lowSlBal > 0 ? "#ef4444" : "#10b981", bg: lowSlBal > 0 ? "#fff1f2" : "#f0fdf4", border: lowSlBal > 0 ? "#fecaca" : "#bbf7d0" },
                        ].map(k => (
                            <Col xs={12} sm={6} key={k.label}>
                                <div style={{
                                    background: k.bg, border: `1px solid ${k.border}`,
                                    borderRadius: 10, padding: "7px 10px", textAlign: "center",
                                }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: k.color, textTransform: "uppercase", marginBottom: 2 }}>
                                        {k.label}
                                    </div>
                                    <div style={{ fontSize: 15, fontWeight: 900, color: k.color }}>{k.val}</div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}

                {loading ? (
                    <div style={{ textAlign: "center", padding: "60px 0" }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>
                            Loading Leave Utilization Data...
                        </div>
                    </div>
                ) : filteredData.length === 0 ? (
                    <Empty description="No leave balance records found" style={{ padding: "50px 0" }} />
                ) : (
                    <>
                        <div ref={tableWrapperRef} className="leave-util-table-wrap">
                            <style>{`
                                .leave-util-table-wrap .ant-table-content,
                                .leave-util-table-wrap .ant-table-body {
                                    overflow-x: auto !important;
                                    margin-bottom: 4px !important;
                                }
                                .leave-util-table-wrap .ant-table-content::-webkit-scrollbar,
                                .leave-util-table-wrap .ant-table-body::-webkit-scrollbar,
                                .leave-util-table-wrap ::-webkit-scrollbar {
                                    height: 12px !important;
                                    display: block !important;
                                }
                                .leave-util-table-wrap .ant-table-content::-webkit-scrollbar-track,
                                .leave-util-table-wrap .ant-table-body::-webkit-scrollbar-track,
                                .leave-util-table-wrap ::-webkit-scrollbar-track {
                                    background: #e2e8f0 !important;
                                    border-radius: 6px !important;
                                }
                                .leave-util-table-wrap .ant-table-content::-webkit-scrollbar-thumb,
                                .leave-util-table-wrap .ant-table-body::-webkit-scrollbar-thumb,
                                .leave-util-table-wrap ::-webkit-scrollbar-thumb {
                                    background: #64748b !important;
                                    border-radius: 6px !important;
                                    border: 2px solid #ffffff !important;
                                }
                                .leave-util-table-wrap .ant-table-content::-webkit-scrollbar-thumb:hover,
                                .leave-util-table-wrap .ant-table-body::-webkit-scrollbar-thumb:hover,
                                .leave-util-table-wrap ::-webkit-scrollbar-thumb:hover {
                                    background: #334155 !important;
                                }
                            `}</style>
                            <Table
                                dataSource={pageData}
                                columns={columns}
                                rowKey={r => r.employee_id || r.employee_code}
                                pagination={false}
                                size="small"
                                scroll={{ x: 1000 }}
                                onRow={(rec) => ({
                                    style: {
                                        background: (rec.cl_balance <= 1 || rec.sl_balance <= 1)
                                            ? "#fff7f7"
                                            : (rec.cl_balance <= 2 || rec.sl_balance <= 2)
                                            ? "#fffdf0"
                                            : "#ffffff",
                                    },
                                })}
                            />
                        </div>

                        {/* Real Draggable Grey Horizontal Scrollbar Track (Before Pagination) */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            margin: "8px 0 4px 0",
                            padding: "4px 8px",
                            background: "#f8fafc",
                            borderRadius: 8,
                            border: "1px solid #cbd5e1"
                        }}>
                            <Button
                                size="small"
                                icon={<LeftOutlined />}
                                onClick={() => scrollTable("left")}
                                style={{ borderRadius: 6, borderColor: "#94a3b8", color: "#334155" }}
                            />
                            <div
                                ref={greyTrackRef}
                                onScroll={handleTrackScroll}
                                className="real-grey-scroll-track"
                                style={{
                                    flex: 1,
                                    overflowX: "scroll",
                                    overflowY: "hidden",
                                    height: 16,
                                    borderRadius: 6,
                                    background: "#e2e8f0",
                                    cursor: "pointer"
                                }}
                            >
                                <style>{`
                                    .real-grey-scroll-track::-webkit-scrollbar {
                                        height: 12px !important;
                                        display: block !important;
                                    }
                                    .real-grey-scroll-track::-webkit-scrollbar-track {
                                        background: #e2e8f0 !important;
                                        border-radius: 6px !important;
                                    }
                                    .real-grey-scroll-track::-webkit-scrollbar-thumb {
                                        background: #64748b !important;
                                        border-radius: 6px !important;
                                        border: 2px solid #ffffff !important;
                                    }
                                    .real-grey-scroll-track::-webkit-scrollbar-thumb:hover {
                                        background: #334155 !important;
                                    }
                                `}</style>
                                <div style={{ width: 1100, height: 1 }} />
                            </div>
                            <Button
                                size="small"
                                icon={<RightOutlined />}
                                onClick={() => scrollTable("right")}
                                style={{ borderRadius: 6, borderColor: "#94a3b8", color: "#334155" }}
                            />
                        </div>


                        {/* Pagination Bar AFTER the Table Scrollbar */}
                        <div style={{
                            display: "flex",
                            justify: "space-between",
                            alignItems: "center",
                            marginTop: 8,
                            paddingTop: 8,
                            borderTop: "1px solid #f1f5f9"
                        }}>
                            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredData.length)} of {filteredData.length} records
                            </div>
                            <Pagination
                                size="small"
                                current={currentPage}
                                pageSize={pageSize}
                                total={filteredData.length}
                                onChange={setCurrentPage}
                                showSizeChanger={false}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}





